import {
    addDays,
    differenceInCalendarDays,
    safeFormatClientDate,
    safeFormatISODate,
    safeParseClientDate,
    safeParseISO,
    subDays,
} from 'shared/util/datetime-fns';
import type { CheckboxMap, CheckboxTree, Destinations, FlexibleDate, Month, NumberRange } from '@/interfaces';

const isLegalMonth = (month: number, year: number): boolean => {
    if (month < 1 || month > 12) {
        return false;
    }

    const firstMonth = new Date().getMonth() + 1;
    const firstYear = new Date().getFullYear();

    if (year < firstYear || year > firstYear + 1) {
        return false;
    }
    if (month < firstMonth && year === firstYear) {
        return false;
    }
    if (month >= firstMonth && year === firstYear + 1) {
        return false;
    }

    return true;
};

const getAllParents = (location: string): string[] => {
    const result = [];
    const all = location.split(';');
    while (all.length > 1) {
        result.push(all.join(';'));
        all.pop();
    }
    return result.reverse();
};

const toggleNestedCheckbox = (tree: CheckboxTree, path: string[]): CheckboxTree => {
    const [head, ...rest] = path;
    if (rest.length <= 0) {
        const { [head]: toggledOn, ...final } = tree;
        return toggledOn ? final : { ...final, [head]: {} };
    }
    return { ...tree, [head]: toggleNestedCheckbox(tree[head] || {}, rest) };
};

export const queryToCheckboxMap = (toMap: string[] | string): CheckboxMap =>
    Array.isArray(toMap)
        ? toMap.reduce((map: CheckboxMap, item: string): CheckboxMap => {
              map[item] = true;
              return map;
          }, {})
        : { [toMap]: true };

export const queryToCheckboxTree = (toMap: string[] | string): CheckboxTree =>
    Array.isArray(toMap)
        ? toMap.reduce((hierarchy: CheckboxTree, item: string): CheckboxTree => toggleNestedCheckbox(hierarchy, getAllParents(item)), {})
        : toggleNestedCheckbox({}, getAllParents(toMap));

export const queryToString = (asString: string[] | string): string => (Array.isArray(asString) ? asString[0] : asString);

export const queryToList = (asString: string[] | string): string[] => (Array.isArray(asString) ? asString : [asString]);

const filterExtensions = (destinations: string[]): string[] =>
    destinations.filter(
        (destination) =>
            !destinations.some((other) => destination.startsWith(other) && destination !== other && destination[other.length] === ';'),
    );

export const queryToDestinations = (query: string[] | string): Destinations => ({
    list: queryToList(query),
    tree: queryToCheckboxTree(filterExtensions(queryToList(query))),
});

export const queryToNumberRange = (toRange: string[] | string): NumberRange | null => {
    const [start, end] = (Array.isArray(toRange) ? toRange[0] : toRange).split('-');
    return ~~end ? [~~start, ~~end] : null;
};

export const queryToFlexibleDate = (toRange: string[] | string): FlexibleDate => {
    const [start, end] = (Array.isArray(toRange) ? toRange[0] : toRange).split('-');
    const parsedStart = safeParseClientDate(start);

    if (end) {
        const parsedEnd = safeParseClientDate(end);
        const diff = differenceInCalendarDays(parsedStart, parsedEnd);

        if (diff) {
            const flexibility = diff % 2 !== 0 ? (diff + 1) / 2 : diff / 2;

            return {
                date: safeFormatISODate(addDays(flexibility, parsedStart)) || undefined,
                flexibility,
            };
        }
    }

    return { date: safeFormatISODate(parsedStart) || undefined, flexibility: 0 };
};

export const queryToMonths = (toMonths: string[] | string): Month[] => {
    const months = Array.isArray(toMonths) ? toMonths : [toMonths];

    return months
        .map((entry: string) => {
            const [month, year] = entry.split('.');
            return isLegalMonth(~~month, ~~year) ? { month: ~~month, year: ~~year } : null;
        })
        .filter((entry: Month | null) => entry !== null) as Month[];
};

export const stringToQuery = (text: string): string => text;

export const listToQuery = (list: string[]): string[] => list;

export const checkboxMapToQuery = (map: CheckboxMap): string[] | null => {
    const values = Object.keys(map).filter((key) => map[key]);
    return values.length === 0 ? null : values;
};

// Finds every leaf node in the tree and returns them as a list
export const checkboxTreeToQuery = (map: CheckboxTree): string[] | null => {
    const values = Object.keys(map)
        .map((key) => (Object.keys(map[key]).length === 0 ? [key] : checkboxTreeToQuery(map[key])) || [])
        .reduce((acc, val) => acc.concat(val), []);
    return values.length === 0 ? null : values;
};

export const destinationsToQuery = (destinations: Destinations): string[] | null =>
    destinations.list.length === 0 ? checkboxTreeToQuery(destinations.tree) : listToQuery(destinations.list);

export const numberRangeToQuery = (range?: NumberRange): string | null => (range ? `${range[0]}-${range[1]}` : null);

export const flexibleDateToQuery = (flexDate?: FlexibleDate): string | null => {
    if (flexDate?.date) {
        const parsedDate = safeParseISO(flexDate.date);

        if (flexDate.flexibility) {
            const startDate = safeFormatClientDate(subDays(flexDate.flexibility, parsedDate));
            const endDate = safeFormatClientDate(addDays(flexDate.flexibility, parsedDate));

            return `${startDate}-${endDate}`;
        }
        return safeFormatClientDate(parsedDate, null);
    }

    return null;
};

export const monthsToQuery = (months: Month[]): string[] | null => (months.length === 0 ? null : months.map((e) => `${e.month}.${e.year}`));
